import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import { DemoToggleButtonGroupControl } from '@/components/Demo';

import GridDemo from './GridDemo';
import BaseItem from './Item';

import {
  LabeledColumn,
  BaseCodeWrapper,
  LabeledRow,
} from './Grid.shared';

function TwoLineCenter() {
  return (
    <GridDemo
      initialValues={{
        placeContent: 'center',
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      defaultPerspectiveEnabled={false}
      controls={[
        <ControlWrapper key="placeContent">
          <DemoToggleButtonGroupControl
            id="placeContent"
            label="place-content"
            options={['start', 'center', 'end']}
          />
        </ControlWrapper>,
      ]}
    >
      {({ placeContent, isPerspectiveEnabled }) => {
        const cssCode = `\
.parent {
  display: grid;
  place-content: ${placeContent};
}`;

        return (
          <Row>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
            <DemoWrapper layout layoutRoot>
              <BackLayer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                {/* Background (with bg) */}
                <RowStructure style={{ alignContent: placeContent }}>
                  <LabeledRow
                    layout={!isPerspectiveEnabled}
                    style={{ gridColumn: '1 / 3', gridRow: 1 }}
                  >
                    R1
                  </LabeledRow>
                </RowStructure>
                <ColumnStructure
                  style={{
                    justifyContent: placeContent,
                  }}
                >
                  <LabeledColumn
                    layout={!isPerspectiveEnabled}
                    style={{ gridColumn: 1, gridRow: '1 / 3' }}
                  >
                    C1
                  </LabeledColumn>
                </ColumnStructure>

                {/* Foreground (no bg) */}
                <RowStructure style={{ alignContent: placeContent }}>
                  <LabeledRow
                    layout={!isPerspectiveEnabled}
                    style={{
                      gridColumn: '1 / 3',
                      gridRow: 1,
                      background: 'transparent',
                    }}
                  >
                    R1
                  </LabeledRow>
                </RowStructure>
                <ColumnStructure
                  style={{
                    justifyContent: placeContent,
                  }}
                >
                  <LabeledColumn
                    layout={!isPerspectiveEnabled}
                    style={{
                      gridColumn: 1,
                      gridRow: '1 / 3',
                      background: 'transparent',
                    }}
                  >
                    C1
                  </LabeledColumn>
                </ColumnStructure>
              </BackLayer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper
                  style={{
                    placeContent,
                  }}
                >
                  <Item
                    layout={!isPerspectiveEnabled}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  >
                    Child
                  </Item>
                </ChildWrapper>
              </Layer>
            </DemoWrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const ControlWrapper = styled.div`
  width: 100%;
  padding-bottom: 16px;
`;

const Row = styled.div`
  --child-size: 60%;
  display: flex;
  gap: 4rem;
  margin: 48px 0px 16px;
  isolation: isolate;

  @media (max-width: 47.5rem) {
    flex-direction: column-reverse;
    gap: 2rem;
    align-items: center;
    margin: 60px 0px 16px;
  }
`;

const DemoWrapper = styled(motion.div)`
  position: relative;
  width: 260px;
  max-width: calc(100vw - 108px);
  aspect-ratio: 1 / 1;

  @media (max-width: 47.5rem) {
    width: 386px;
  }
`;

const Layer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const BackLayer = styled(Layer)`
  display: grid;
  padding: 2px;
  border-radius: 4px;
  outline: 1px solid var(--color-gray-300);
  background: repeating-linear-gradient(
    45deg,
    hsl(210deg 40% 80% / 0.06) 0px,
    hsl(210deg 40% 80% / 0.06) 10px,
    hsl(210deg 40% 80% / 0) 10px,
    hsl(210deg 40% 80% / 0) 20px
  );
`;

const ColumnStructure = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-columns: var(--child-size);
  gap: 0px;
  height: 100%;
`;
const RowStructure = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  grid-template-rows: var(--child-size);
  gap: 0px;
  width: 100%;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: var(--child-size);
  grid-template-rows: var(--child-size);
  gap: 0px;
  padding: 2px;
`;

const Item = styled(BaseItem)`
  margin: 3px;
  font-size: 0.875rem;
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  align-self: flex-start;
  position: relative;
  z-index: 2;
  width: 388px;
  flex-grow: 1;

  @media (max-width: 47.5rem) {
    align-self: center;
  }

  @media (max-width: 27.5rem) {
    width: calc(100% + 32px);
    max-width: calc(100% + 32px);
    margin-inline: -16px;
    padding-inline: 3px;
    border-radius: 0;
  }
`;

export default TwoLineCenter;
